/**
 * Motion — eDMS Design System (UX bolder-refresh, primitive #5)
 *
 *   <FadeIn>   — fades + lifts its children in on mount.
 *   <Stagger>  — wraps each child in a FadeIn with an incremental delay, for
 *                staggered card/list reveals.
 *
 * Both honour prefers-reduced-motion (via usePrefersReducedMotion): when reduced,
 * children render immediately with no transform/opacity animation. FadeIn renders
 * a block-level wrapper element (configurable via `as`).
 */
import { Children, type CSSProperties, type ElementType, type ReactNode } from 'react';
import { usePrefersReducedMotion } from '../../hooks/usePrefersReducedMotion';
import styles from './Motion.module.css';

export interface FadeInProps {
  children: ReactNode;
  /** Delay before this element animates, in ms. Default 0. */
  delay?: number;
  /** Animation duration, in ms. Default 400. */
  duration?: number;
  /** Initial vertical offset in px (slides up to 0). Default 8. */
  y?: number;
  /** Wrapper element. Default 'div'. */
  as?: ElementType;
  className?: string;
  style?: CSSProperties;
}

export function FadeIn({
  children,
  delay = 0,
  duration = 400,
  y = 8,
  as,
  className,
  style,
}: FadeInProps) {
  const reduced = usePrefersReducedMotion();
  const Tag: ElementType = as ?? 'div';

  if (reduced) {
    return (
      <Tag className={className} style={style}>
        {children}
      </Tag>
    );
  }

  const motionStyle: CSSProperties = {
    animationDelay: `${delay}ms`,
    animationDuration: `${duration}ms`,
    ['--fade-y' as string]: `${y}px`,
    ...style,
  };
  return (
    <Tag className={className ? `${styles.fadeIn} ${className}` : styles.fadeIn} style={motionStyle}>
      {children}
    </Tag>
  );
}

export interface StaggerProps {
  children: ReactNode;
  /** Milliseconds between consecutive children. Default 60. */
  step?: number;
  /** Milliseconds before the first child. Default 0. */
  initialDelay?: number;
  /** Per-child entrance duration, in ms. Default 400. */
  duration?: number;
  /** Initial vertical offset in px. Default 8. */
  y?: number;
  /** Container element. Default 'div'. */
  as?: ElementType;
  className?: string;
  style?: CSSProperties;
}

export function Stagger({
  children,
  step = 60,
  initialDelay = 0,
  duration = 400,
  y = 8,
  as,
  className,
  style,
}: StaggerProps) {
  const Tag: ElementType = as ?? 'div';
  const items = Children.toArray(children);
  return (
    <Tag className={className} style={style}>
      {items.map((child, i) => (
        <FadeIn key={i} delay={initialDelay + i * step} duration={duration} y={y}>
          {child}
        </FadeIn>
      ))}
    </Tag>
  );
}
